import {
    Alert,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useMemo, useState} from 'react';
import {useProfile, useProfiles} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {useAllReservations} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import ProfileInfoRow from "crqlar-react-common/dist/components/ProfileInfoRow";
import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {FOLIO_ALERT_SX, FOLIO_FIELD_GAP} from "../editor/folioStyles";

const MEWS_PROVIDER_KEY = 'mews';
const ACCOUNT_ONLY = '__account_only__';
const OTHER_PROFILE = '__other_profile__';

interface MewsProviderProps {
    onDataChange: (isValid: boolean, data: any) => void;
    profileId?: string;
    folio?: Folio;
    /**
     * The folio dialog's payment block: less space, a small alert, no title repeating the chosen
     * payment method (the profile search is the shared row and keeps its size).
     */
    compact?: boolean;
}

const MewsProvider = ({profileId, folio, onDataChange, compact}: MewsProviderProps) => {
    const {t} = useTranslation();

    const reservation = useAllReservations(folio?.reservationId ? [folio.reservationId] : [])?.[0];
    const reservationProfileIds = reservation?.profileIds ?? [];
    const reservationProfiles = useProfiles(reservationProfileIds);

    const defaultProfileId = folio?.profileId ?? reservation?.primaryProfileId ?? profileId;

    // profileSource: either a profile id from reservation, or OTHER_PROFILE when using search
    const [profileSource, setProfileSource] = useState<string | null>(null);
    const [otherProfileId, setOtherProfileId] = useState<string | undefined>(undefined);

    const selectedProfileId = profileSource === OTHER_PROFILE ? otherProfileId : (profileSource ?? undefined);
    const selectedProfile = useProfile(selectedProfileId);

    const [selectedReservationId, setSelectedReservationId] = useState<string>(ACCOUNT_ONLY);

    const reservationProfileIdsKey = reservationProfileIds.join('|');
    // When the folio references a reservation, wait for it to load before deciding
    // whether the default profile is on-reservation or an "Other" profile.
    const reservationPending = !!folio?.reservationId && !reservation;

    // Initialize profile source once reservation/folio data is known
    useEffect(() => {
        if (profileSource !== null) return;
        if (reservationPending) return;
        if (!defaultProfileId) return;

        if (reservationProfileIds.includes(defaultProfileId)) {
            setProfileSource(defaultProfileId);
        } else {
            setProfileSource(OTHER_PROFILE);
            setOtherProfileId(defaultProfileId);
        }
    }, [defaultProfileId, reservationProfileIdsKey, profileSource, reservationPending]);

    // Auto-select stay when profile changes/loads
    useEffect(() => {
        if (!selectedProfile) return;
        const activeStays = selectedProfile.activeStays ?? [];

        if (folio?.reservationId && activeStays.some(s => s.reservationId === folio.reservationId)) {
            setSelectedReservationId(folio.reservationId);
            return;
        }

        const currentStayResId = selectedProfile.currentStay?.reservationId;
        if (currentStayResId) {
            setSelectedReservationId(currentStayResId);
            return;
        }

        setSelectedReservationId(ACCOUNT_ONLY);
    }, [selectedProfile?.id, folio?.reservationId]);

    const hasMewsExternalInfo = useMemo(
        () => !!selectedProfile?.externalInfo?.find(e => e.provider === MEWS_PROVIDER_KEY),
        [selectedProfile?.externalInfo]
    );

    useEffect(() => {
        const reservationIdToEmit = selectedReservationId === ACCOUNT_ONLY ? undefined : selectedReservationId;
        const isValid = !!selectedProfileId && hasMewsExternalInfo;
        onDataChange(isValid, {
            provider: MEWS_PROVIDER_KEY,
            profileId: selectedProfileId,
            reservationId: reservationIdToEmit
        });
    }, [selectedProfileId, selectedReservationId, hasMewsExternalInfo]);

    const activeStays = selectedProfile?.activeStays ?? [];
    const showReservationProfileToggle = reservationProfileIds.length > 0;
    const showSearch = profileSource === OTHER_PROFILE || !showReservationProfileToggle;

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: compact ? FOLIO_FIELD_GAP : 2, width: '100%'}}>
            {!compact && <Typography>{t('MEWS Provider Configuration')}</Typography>}

            {showReservationProfileToggle && (
                <FormControl>
                    <Typography variant="subtitle2" gutterBottom>
                        {t('Post charge to profile')}
                    </Typography>
                    <ToggleButtonGroup
                        value={profileSource}
                        exclusive
                        onChange={(_, value) => {
                            if (!value) return;
                            setProfileSource(value);
                            if (value !== OTHER_PROFILE) setOtherProfileId(undefined);
                        }}
                        size="small"
                        sx={{flexWrap: 'wrap'}}
                    >
                        {reservationProfileIds.map(pid => {
                            const p = reservationProfiles.find(rp => rp.id === pid);
                            const isPrimary = pid === reservation?.primaryProfileId;
                            const label = formatProfileListName(p) || pid;
                            return (
                                <ToggleButton key={pid} value={pid} sx={{textTransform: 'none'}}>
                                    {label}{isPrimary ? ` (${t('Primary')})` : ''}
                                </ToggleButton>
                            );
                        })}
                        <ToggleButton value={OTHER_PROFILE} sx={{textTransform: 'none'}}>
                            {t('Other profile…')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            )}

            {showSearch && (
                <ProfileInfoRow
                    profileId={otherProfileId}
                    setProfileId={(pid) => {
                        if (!showReservationProfileToggle) setProfileSource(OTHER_PROFILE);
                        setOtherProfileId(pid);
                    }}
                    onDelete={() => setOtherProfileId(undefined)}
                    title={t('Mews account holder')}
                />
            )}

            {selectedProfileId && !hasMewsExternalInfo && (
                <Alert severity="error" sx={compact ? FOLIO_ALERT_SX : undefined}>
                    {t('Selected profile is not linked to Mews — charge will fail.')}
                </Alert>
            )}

            {selectedProfileId && hasMewsExternalInfo && (
                <FormControl fullWidth size="small">
                    <InputLabel id="mews-stay-select-label">{t('Link to stay')}</InputLabel>
                    <Select
                        labelId="mews-stay-select-label"
                        label={t('Link to stay')}
                        value={selectedReservationId}
                        onChange={(e) => setSelectedReservationId(e.target.value as string)}
                    >
                        <MenuItem value={ACCOUNT_ONLY}>
                            {t('No reservation (post to account only)')}
                        </MenuItem>
                        {activeStays
                            .filter(stay => !!stay.reservationId)
                            .map(stay => (
                                <MenuItem key={stay.reservationId} value={stay.reservationId!}>
                                    {stay.startDay} → {stay.endDay}
                                    {stay.roomId ? ` · ${t('room')} ${stay.roomId}` : ''}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};

export default MewsProvider;
